import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import { formatCcy } from '../../utils/helpers';
import handleApiResponse from '../../utils/handleApiResponse';
import { getStudentContractsByStudentId } from '../../requests/api/students';
import { postRegisterPayment, getPaymentDocumentsByStudentId } from '../../requests/api/contractPayment';
import { postInitWebpay } from '../../requests/api/enrollment';
import { useStyles } from './styles';
import { Contract, StudentProgram } from './types';
import BranchPaymentModal from './Modals/BranchPaymentModal';
import TransferModal from './Modals/TransferModal';
import Page from '../../components/Layout/Page';
import { PaymentMethod } from '../../utils/payments';

const EnrollmentPayment: React.FC = () => {
  const classes = useStyles();
  const { studentId } = useParams<{ studentId: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [branchPaymentModalOpen, setBranchPaymentModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [hasPendingPayment, setHasPendingPayment] = useState(false);

  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const [contractsResponse, paymentDocumentResponse] = await Promise.all([
          getStudentContractsByStudentId(studentId),
          getPaymentDocumentsByStudentId(studentId),
        ]);
        setContracts(contractsResponse.studentContracts.contracts || []);
        setHasPendingPayment(paymentDocumentResponse?.exists || false);
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
        history.push(`/inscripcion/alumno/${studentId}`);
      } finally {
        setLoading(false);
      }
    };

    fetchContracts();
  }, [studentId, enqueueSnackbar, history]);

  if (loading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!contracts || contracts.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6" color="error">
          No se encontró el contrato del estudiante
        </Typography>
      </Box>
    );
  }

  const contract = contracts[0];

  const allPrograms: StudentProgram[] = contracts.flatMap(
    (c) => c.studentPrograms || [],
  );
  const tuitionValue = contract.tuition || 0;
  const tuitionDiscountPercent = contract.tuitionDiscount || 0;
  const finalTuitionValue = tuitionValue * (1 - tuitionDiscountPercent / 100);

  const totalFee = allPrograms.reduce(
    (sum, program) => sum + (program.program.fee - program.feeDiscount),
    0,
  );

  const feeDiscount = contract.feeDiscount || 0;
  const feeAdditionalDiscount = contract.feeAdditionalDiscount || 0;
  const feePaymentTypeDiscount = contract.feePaymentTypeDiscount || 0;

  let feeDiscountAmount = 0;
  if (feeDiscount > 0) {
    feeDiscountAmount = totalFee * (feeDiscount / 100);
  }
  const totalFeeDiscount = feeDiscountAmount + feeAdditionalDiscount
    + (totalFee * (feePaymentTypeDiscount / 100));
  const finalFeeValue = totalFee - totalFeeDiscount;

  const handleBranchPaymentAccept = () => {
    setBranchPaymentModalOpen(false);
  };

  const handleTransferConfirm = async (file: File) => {
    try {
      await postRegisterPayment({
        contractId: contract.id.toString(),
        amount: finalTuitionValue,
        comment: 'Pago de matrícula por transferencia',
        type: 'tuition',
        file,
        paymentMethod: PaymentMethod.TRANSFER,
      });

      enqueueSnackbar('Comprobante enviado exitosamente. Tu matrícula ha sido confirmada', { variant: 'success' });
      setTransferModalOpen(false);
      setTimeout(() => {
        history.push(`/alumnos/${studentId}`);
      }, 1500);
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  const handleOnlinePayment = async () => {
    try {
      const response = await postInitWebpay(studentId);

      if (response && response.url && response.token) {
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = response.url;
        form.style.display = 'none';

        const tokenInput = document.createElement('input');
        tokenInput.type = 'hidden';
        tokenInput.name = 'token_ws';
        tokenInput.value = response.token;

        form.appendChild(tokenInput);
        document.body.appendChild(form);
        form.submit();
      } else {
        enqueueSnackbar('Error al inicializar el pago. Por favor, intenta nuevamente.', { variant: 'error' });
      }
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  return (
    <Page title="Confirma tu plan">
      <Box className={classes.container}>
        <Box className={classes.backButtonContainer}>
          <IconButton
            onClick={() => history.push(`/inscripcion/alumno/${studentId}`)}
            className={classes.backButtonIcon}
            size="small"
          >
            <ArrowBackIos className={classes.backIcon} />
          </IconButton>
          <Typography className={classes.pageTitle}>
            Confirma tu plan
          </Typography>
        </Box>

        <Box className={classes.summaryContainer}>
          <Typography className={classes.mainTitle}>Resumen</Typography>
          {allPrograms.map((studentProgram) => (
            <Box className={classes.itemBox} key={studentProgram.id}>
              <Box className={classes.itemLeft}>
                <Typography className={classes.title}>
                  {studentProgram.program.name}
                </Typography>
              </Box>
              <Box className={classes.itemRight}>
                <Typography className={classes.title}>
                  Arancel Final
                </Typography>
                {studentProgram.feeDiscount > 0 && (
                  <Typography className={classes.discountText}>
                    {formatCcy(studentProgram.feeDiscount, '.')} OFF
                  </Typography>
                )}
                <Typography className={classes.priceText}>
                  {formatCcy(studentProgram.program.fee - studentProgram.feeDiscount, '.')}
                </Typography>
              </Box>
            </Box>
          ))}

          <Divider />

          <Box className={classes.itemBox}>
            <Box className={classes.itemLeft}>
              <Typography className={classes.title}>
                Valor Arancel
              </Typography>
            </Box>
            <Box className={classes.itemRight}>
              <Typography className={classes.title}>Total</Typography>
              {(feeDiscount > 0 || feeAdditionalDiscount > 0 || feePaymentTypeDiscount > 0) && (
                <Typography className={classes.discountText}>
                  {formatCcy(totalFee - finalFeeValue, '.')}
                </Typography>
              )}
              <Typography className={classes.priceText}>
                {formatCcy(finalFeeValue, '.')}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box className={classes.itemBox}>
            <Box className={classes.itemLeft}>
              <Typography className={classes.title}>
                Valor Matrícula
              </Typography>
            </Box>
            <Box className={classes.itemRight}>
              <Typography className={classes.title}>Total</Typography>
              {tuitionDiscountPercent > 0 && (
                <Typography className={classes.discountText}>
                  {formatCcy(tuitionValue, '.')}
                </Typography>
              )}
              <Typography className={classes.priceText}>
                {formatCcy(finalTuitionValue, '.')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography className={classes.warningText}>
          Para reservar tu cupo, tienes que pagar TU MATRÍCULA a continuación
        </Typography>

        <Box className={classes.finalTotalBox}>
          <Typography className={classes.totalLabel}>
            Total a pagar
          </Typography>
          <Typography className={classes.totalValue}>
            {formatCcy(finalTuitionValue, '.')}
          </Typography>
        </Box>

        <Divider />

        <Box className={classes.paymentButtonsContainer}>
          {hasPendingPayment && (
            <Box className={classes.warningTextContainer}>
              <Typography variant="body2">
                Ya tienes un pago pendiente de validación. Por favor espera a que sea revisado.
              </Typography>
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            className={classes.paymentButton}
            fullWidth
            onClick={handleOnlinePayment}
            disabled={loading || hasPendingPayment}
          >
            Pagar online (Webpay)
          </Button>
          <Button
            variant="contained"
            color="primary"
            className={classes.paymentButton}
            fullWidth
            onClick={() => setTransferModalOpen(true)}
            disabled={hasPendingPayment}
          >
            Realizar transferencia
          </Button>
          <Button
            variant="outlined"
            color="primary"
            className={classes.paymentButton}
            fullWidth
            onClick={() => setBranchPaymentModalOpen(true)}
            disabled={hasPendingPayment}
          >
            Pagar en sucursal C&C
          </Button>
        </Box>

        <BranchPaymentModal
          open={branchPaymentModalOpen}
          onClose={() => setBranchPaymentModalOpen(false)}
          onAccept={handleBranchPaymentAccept}
        />

        <TransferModal
          open={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
          amount={formatCcy(finalTuitionValue, '.')}
          onConfirm={handleTransferConfirm}
        />
      </Box>
    </Page>
  );
};

export default EnrollmentPayment;
